import { Col, Row, Skeleton, Tooltip, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import type { CSSProperties, ReactNode } from 'react'
import {
    IoArrowDown,
    IoArrowUp,
    IoCheckmarkDoneOutline,
    IoDocumentTextOutline,
    IoPieChartOutline,
    IoTimeOutline,
    IoTrendingUpOutline,
    IoWalletOutline,
} from 'react-icons/io5'

import { classNames } from '@/Ship/Helpers/classNames'
import { dashboardManager } from '@/Ship/Stores/DashboardManager'

import type { IOverviewKpiWindow } from '@/Ship/Contracts/IHomeOverview'

import { formatInt, formatMoney, formatMoneyShort, formatShare, shortDate, useChartColors } from './shared'
import styles from './Overview.module.css'

type KpiKey = keyof IOverviewKpiWindow

/** Показники згори — у порядку, в якому їх читає керівник: потік, виконання, гроші, якість. */
export const KPI_TILES: ReadonlyArray<{ key: KpiKey; title: string; hint: string; kind: 'count' | 'money' | 'share' }> =
    [
        {
            key: 'created',
            title: 'Нові заявки',
            hint: 'Заявки, створені за обраний період, зокрема програні: вхідний потік.',
            kind: 'count',
        },
        {
            key: 'closed',
            title: 'Виконано рейсів',
            hint: 'Не програні заявки з фактичним розвантаженням за обраний період.',
            kind: 'count',
        },
        {
            key: 'turnover',
            title: 'Оборот',
            hint: 'Вартість для клієнтів у гривнях за курсом прибутку заявки.',
            kind: 'money',
        },
        { key: 'profit', title: 'Прибуток', hint: 'Сума прибутку виконаних перевезень.', kind: 'money' },
        { key: 'margin', title: 'Маржа', hint: 'Прибуток / оборот — лише заявки, де відомі обидва.', kind: 'share' },
        {
            key: 'onTime',
            title: 'Вчасна доставка',
            hint: 'Частка розвантажень не пізніше дати ПЛАН серед заявок, де її заповнено.',
            kind: 'share',
        },
    ]

const ICONS: Record<KpiKey, ReactNode> = {
    created: <IoDocumentTextOutline />,
    closed: <IoCheckmarkDoneOutline />,
    turnover: <IoWalletOutline />,
    profit: <IoTrendingUpOutline />,
    margin: <IoPieChartOutline />,
    onTime: <IoTimeOutline />,
}

const format = (kind: 'count' | 'money' | 'share', value: number | null): string =>
    value === null
        ? '—'
        : kind === 'money'
          ? // За квартал чи рік оборот — десятки мільйонів і не влазить у плитку:
            // від мільйона — коротко («10,8 млн ₴»), точна сума — у підказці значення
            Math.abs(value) >= 1_000_000
              ? formatMoneyShort(value)
              : formatMoney(value)
          : kind === 'share'
            ? formatShare(value)
            : formatInt(value)

/**
 * Зміна проти того самого відрізка попереднього періоду: для кількостей і грошей —
 * відсотки, для часток — процентні пункти. Без бази — `null` (не «+∞ %»).
 */
export const kpiDelta = (
    kind: 'count' | 'money' | 'share',
    current: number | null,
    previous: number | null
): { value: number; label: string } | null => {
    if (current === null || previous === null) return null

    if (kind === 'share') {
        const diff = Math.round((current - previous) * 10) / 10

        return { value: diff, label: `${diff > 0 ? '+' : ''}${diff.toLocaleString('uk-UA')} п.п.` }
    }

    if (previous === 0) return null

    const pct = Math.round(((current - previous) / previous) * 100)

    return { value: pct, label: `${pct > 0 ? '+' : ''}${pct} %` }
}

/** Міні-лінія тренду — SVG без бібліотеки: шість таких на сторінці не варті окремого шматка збірки. */
const Sparkline = ({ values, color }: { values: number[]; color: string }) => {
    if (values.length < 2 || values.every((value) => value === 0)) return null

    const width = 84
    const height = 30
    const max = Math.max(...values)
    const min = Math.min(...values, 0)
    const span = max - min || 1
    const points = values.map(
        (value, index) =>
            [(index / (values.length - 1)) * width, height - 3 - ((value - min) / span) * (height - 6)] as const
    )
    const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
    const last = points[points.length - 1]!

    return (
        <svg className={classNames(styles['spark'])} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            <polygon points={`0,${height} ${line} ${width},${height}`} fill={color} fillOpacity={0.12} />
            <polyline
                points={line}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} />
        </svg>
    )
}

/**
 * Шість показників обраного періоду до сьогодні з порівнянням проти того самого
 * відрізка попереднього періоду й міні-трендом за 12 місяців (де тренд є).
 * Колір значення — колір тексту; зелений чи червоний несе лише зміна, і поруч
 * завжди стрілка й знак — не самим кольором.
 */
export const KpiStrip = observer(() => {
    const { token } = theme.useToken()
    const chart = useChartColors()
    const { overview, isOverviewLoading } = dashboardManager
    const accents: Record<KpiKey, string> = {
        created: chart.primary,
        closed: chart.third,
        turnover: chart.fourth,
        profit: token.colorSuccess,
        margin: chart.second,
        onTime: chart.sixth,
    }
    const trend = overview?.trend ?? []
    const sparks: Partial<Record<KpiKey, number[]>> = {
        closed: trend.map((point) => point.count),
        turnover: trend.map((point) => point.turnover),
        profit: trend.map((point) => point.profit),
        margin: trend.map((point) => point.margin ?? 0),
    }
    const compared = overview
        ? `проти ${shortDate(overview.kpi.previousFrom)}–${shortDate(overview.kpi.previousTo)}`
        : ''

    return (
        <Row gutter={[16, 16]} data-block="overview-kpi">
            {KPI_TILES.map((tile) => {
                const current = overview ? overview.kpi.current[tile.key] : null
                const delta = overview ? kpiDelta(tile.kind, current, overview.kpi.previous[tile.key]) : null
                const accent = accents[tile.key]

                return (
                    <Col key={tile.key} xs={24} sm={12} lg={8} xl={4}>
                        <div
                            className={classNames(styles['kpi'])}
                            style={{ '--kpi-accent': accent } as CSSProperties}
                            data-kpi={tile.key}
                        >
                            <Tooltip title={tile.hint} placement="topLeft">
                                <div className={classNames(styles['kpiLabel'])}>
                                    <span
                                        className={classNames(styles['kpiIcon'])}
                                        style={{ color: accent, background: token.colorFillTertiary }}
                                    >
                                        {ICONS[tile.key]}
                                    </span>
                                    {tile.title}
                                </div>
                            </Tooltip>
                            {isOverviewLoading && !overview ? (
                                <Skeleton
                                    active
                                    title={false}
                                    paragraph={{ rows: 2, width: ['70%', '45%'] }}
                                    style={{ marginTop: 8 }}
                                />
                            ) : (
                                <>
                                    <div
                                        className={classNames(styles['kpiValue'])}
                                        data-kpi-value
                                        {...(tile.kind === 'money' && current !== null
                                            ? { title: formatMoney(current) }
                                            : {})}
                                    >
                                        {format(tile.kind, current)}
                                    </div>
                                    <div className={classNames(styles['kpiFoot'])}>
                                        <div>
                                            {delta ? (
                                                <span
                                                    className={classNames(styles['delta'])}
                                                    style={{
                                                        color:
                                                            delta.value === 0
                                                                ? token.colorTextSecondary
                                                                : delta.value > 0
                                                                  ? token.colorSuccess
                                                                  : token.colorError,
                                                    }}
                                                >
                                                    {delta.value > 0 ? (
                                                        <IoArrowUp />
                                                    ) : delta.value < 0 ? (
                                                        <IoArrowDown />
                                                    ) : null}
                                                    {delta.label}
                                                </span>
                                            ) : (
                                                <span
                                                    className={classNames(styles['delta'])}
                                                    style={{ color: token.colorTextTertiary }}
                                                >
                                                    немає бази
                                                </span>
                                            )}
                                            <span className={classNames(styles['deltaNote'])}>{compared}</span>
                                        </div>
                                        <Sparkline values={sparks[tile.key] ?? []} color={accent} />
                                    </div>
                                </>
                            )}
                        </div>
                    </Col>
                )
            })}
        </Row>
    )
})
